import type { HomeDto } from '@lmc/ts-common'
import {
  Button,
  Card,
  DownloadFile,
  EmptyState,
  FilterContainer,
  GroupedUsersMultiSelect,
  Heading,
  MultiSelect,
  NoDataIcon,
  PageHeader
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Table } from 'antd'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  type RotaOverviewFilters,
  useRotaOverview
} from 'screens/Rostering/hooks/useRotaOverview'
import {
  TravelRateCardLevel,
  TravelRateCardStatus,
  useGetTravelRateCardSettings
} from 'screens/Rostering/hooks/useTravelRateCardSettings'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import {
  OperationalIssuesIcon,
  TravelAndGapIcon,
  UtilisationAndAttendanceIcon
} from 'screens/Rostering/RotaBuilder/common/components/icons'
import styled from 'styled-components'
import DownloadRotaOverviewModal from '../components/rotaOverview/DownloadRotaOverviewModal'
import WeekRangePicker from '../components/rotaOverview/WeekRangePicker'
import { useExportRotaOverviewCsv } from '../hooks/useExportRotaOverview'
import {
  formatMetersToMiles,
  formatMinutesToHours,
  getOverviewColumns
} from '../utils/getOverviewColumns'

dayjs.extend(isoWeek)

const RotaOverview = () => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<RotaOverviewFilters>({
    from: dayjs().startOf('isoWeek').toDate(),
    to: dayjs().endOf('isoWeek').toDate(),
    teamMembers: [],
    contractTypes: []
  })
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false)

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const flags = useFeatureFlags({
    flagNames: [
      'travel_and_mileage',
      'rostering_overview_report_event_type_breakdown'
    ]
  })?.data

  const flagsTravelTimeAndMileageEnabled = flags?.travel_and_mileage?.enabled

  const flagsRosteringOverviewReportEventTypeBreakdownEnabled =
    flags?.rostering_overview_report_event_type_breakdown?.enabled

  const { data: rateCards } = useGetTravelRateCardSettings()
  const siteRateCard = rateCards?.find(
    (card) => card.levelType === TravelRateCardLevel.SITE
  )

  const contractTypeOptions =
    home.rotaSettings?.contractTypes?.map((contractType) => ({
      label: contractType.name,
      value: contractType.id
    })) || []

  const { users } = useUsersList({})

  const { overview, isLoading: isOverviewLoading } = useRotaOverview(filters)

  const exportCsv = useExportRotaOverviewCsv(filters)

  const handleFilterChange = (newValue: Partial<RotaOverviewFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newValue
    }))
  }

  const columns = getOverviewColumns(
    t,
    flagsTravelTimeAndMileageEnabled,
    siteRateCard
  )

  const handleDownloadCsv = () => {
    if (flagsRosteringOverviewReportEventTypeBreakdownEnabled) {
      setIsDownloadModalVisible(true)
    } else {
      exportCsv()
    }
  }

  return (
    <MainContainer>
      <PageHeader title={t('ROTA_OVERVIEW_REPORT')} />
      <FilterContainer title={t('FILTERS')}>
        <Filters>
          <LeftContainer>
            <WeekRangePicker
              value={[filters.from, filters.to]}
              onChange={(dates) =>
                handleFilterChange({
                  from: dates[0],
                  to: dates[1]
                })
              }
            />
            <GroupedUsersMultiSelect
              users={users}
              title={t('TEAM_MEMBERS')}
              values={filters.teamMembers}
              handleInputChange={(selectedOptions) =>
                handleFilterChange({
                  teamMembers: selectedOptions as string[]
                })
              }
            />
            <StyledMultiSelect
              title={t('CONTRACT_TYPES')}
              options={contractTypeOptions}
              values={filters.contractTypes}
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selectedTypes) =>
                handleFilterChange({
                  contractTypes: selectedTypes as string[]
                })
              }
            />
          </LeftContainer>
          <RightAlign>
            <Button onClick={handleDownloadCsv} icon={<DownloadFile />}>
              {t('DOWNLOAD_CSV')}
            </Button>
          </RightAlign>
        </Filters>
      </FilterContainer>
      <ContentContainer>
        {siteRateCard?.status === TravelRateCardStatus.ENABLED ? (
          <>
            <StyledCard>
              <CardHeader>
                <UtilisationAndAttendanceIcon />
                <CardHeading level={4}>
                  {t('UTILISATION_AND_ATTENDANCE')}
                </CardHeading>
              </CardHeader>
              <Divider />
              <DetailsContainer>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('HOURS_WORKED')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.hoursWorked ||
                      0}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('HOURS_ASSIGNED')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.assignedHours ||
                      0}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('ABSENT_DAYS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.absentDays || 0}{' '}
                    {t('DAYS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('UNDERUTILISED_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.utilisationAndAttendance
                        .underutilisedMinutes || 0
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('OVERTIME_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.utilisationAndAttendance
                        .overtimeMinutes || 0
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
              </DetailsContainer>
            </StyledCard>
            <FlexContainer>
              <FlexCard>
                <CardHeader>
                  <TravelAndGapIcon />
                  <CardHeading level={4}>{t('TRAVEL_AND_GAP')}</CardHeading>
                </CardHeader>
                <Divider />
                <DetailsContainer>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('TRAVEL_TIME')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {formatMinutesToHours(
                        overview?.summary.travelAndGap.travelMinutes || 0
                      )}{' '}
                      {t('HOURS')}
                    </DetailSectionValue>
                  </DetailSection>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('MILEAGE')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {formatMetersToMiles(
                        overview?.summary.travelAndGap.distanceMeters || 0
                      )}{' '}
                      {t('MILES')}
                    </DetailSectionValue>
                  </DetailSection>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('TOTAL_VISIT_GAPS')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {overview?.summary.travelAndGap.totalVisitGaps || 0}{' '}
                      {t('HOURS')}
                    </DetailSectionValue>
                  </DetailSection>
                </DetailsContainer>
              </FlexCard>
              <FlexCard>
                <CardHeader>
                  <OperationalIssuesIcon />
                  <CardHeading level={4}>{t('OPERATIONAL_ISSUES')}</CardHeading>
                </CardHeader>
                <Divider />
                <DetailsContainer>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('EXCEPTIONS')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {overview?.summary.operationalIssues.exceptions || 0}
                    </DetailSectionValue>
                  </DetailSection>
                  <DetailSection>
                    <DetailSectionHeading level={2}>
                      {t('UNALLOCATED_HOURS')}
                    </DetailSectionHeading>
                    <DetailSectionValue level={1} weight="bold">
                      {formatMinutesToHours(
                        overview?.summary.operationalIssues
                          .unallocatedMinutes || 0
                      )}{' '}
                      {t('HOURS')}
                    </DetailSectionValue>
                  </DetailSection>
                </DetailsContainer>
              </FlexCard>
            </FlexContainer>
          </>
        ) : (
          <FlexContainer>
            <FlexCard>
              <CardHeader>
                <UtilisationAndAttendanceIcon />
                <CardHeading level={4}>
                  {t('UTILISATION_AND_ATTENDANCE')}
                </CardHeading>
              </CardHeader>
              <Divider />
              <DetailsContainer>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('HOURS_WORKED')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.hoursWorked ||
                      0}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('HOURS_ASSIGNED')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.assignedHours ||
                      0}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('ABSENT_DAYS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.utilisationAndAttendance.absentDays || 0}{' '}
                    {t('DAYS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('UNDERUTILISED_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.utilisationAndAttendance
                        .underutilisedMinutes || 0
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('OVERTIME_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.utilisationAndAttendance
                        .overtimeMinutes || 0
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
              </DetailsContainer>
            </FlexCard>
            <FlexCard>
              <CardHeader>
                <OperationalIssuesIcon />
                <CardHeading level={4}>{t('OPERATIONAL_ISSUES')}</CardHeading>
              </CardHeader>
              <Divider />
              <DetailsContainer>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('EXCEPTIONS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {overview?.summary.operationalIssues.exceptions || 0}
                  </DetailSectionValue>
                </DetailSection>
                <DetailSection>
                  <DetailSectionHeading level={2}>
                    {t('UNALLOCATED_HOURS')}
                  </DetailSectionHeading>
                  <DetailSectionValue level={1} weight="bold">
                    {formatMinutesToHours(
                      overview?.summary.operationalIssues.unallocatedMinutes ||
                        0
                    )}{' '}
                    {t('HOURS')}
                  </DetailSectionValue>
                </DetailSection>
              </DetailsContainer>
            </FlexCard>
          </FlexContainer>
        )}
        {isOverviewLoading ? (
          <Spinner />
        ) : overview?.teamMemberStats?.length ? (
          <StyledTable
            columns={columns}
            dataSource={overview.teamMemberStats}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => t('TOTAL_ITEMS', { total })
            }}
            scroll={{ x: 'max-content' }}
            loading={false}
            tableLayout="fixed"
          />
        ) : (
          <EmptyState title={t('NO_DATA_TO_SHOW')} icon={<NoDataIcon />} />
        )}
      </ContentContainer>

      <DownloadRotaOverviewModal
        isVisible={isDownloadModalVisible}
        onClose={() => setIsDownloadModalVisible(false)}
        initialFilters={filters}
      />
    </MainContainer>
  )
}

const MainContainer = styled.div`
  max-width: 1080px;
  margin: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-x: auto;
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const FlexContainer = styled.div`
  display: flex;
  gap: 16px;
`

const StyledCard = styled(Card)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FlexCard = styled(StyledCard)`
  flex: 1;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CardHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const DetailsContainer = styled.div`
  display: flex;
`

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const DetailSectionHeading = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const DetailSectionValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const Filters = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

const LeftContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const RightAlign = styled.div`
  display: flex;
  align-items: center;
`

const StyledTable = styled(Table)`
  .ant-table {
    width: 100%;
    min-width: 1000px;
  }

  .ant-table-column-sorters {
    gap: 8px;
    flex-direction: row-reverse;
  }

  .ant-table-cell {
    padding: 16px 12px !important;
  }
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 200px;
`

export default RotaOverview
